import { ServiceLayer } from "./ServiceLayer.js";
import { colorScaleToWmsParams, copyColorScale } from "../../ui/color/Utils.js";
import { xmlToJson } from "../../utils/utils.js";
import { EventObject } from "../../events/EventObject.js";

export { WMSLayer }

/**
 * WMS Layer Implementation
 * 
 * @author rhess <robin.hess@awi.de>
 * @author sjaswal <shahzeib.jaswal@awi.de>
 * @memberof vef.map.layer
 */
class WMSLayer extends ServiceLayer {

    /**
     * Set all properties for the layer based on the options object.
     * 
     * @param {object} config 
     * @param {object} cache 
     * @param {string} id 
     */
    constructor(config, cache, id) {
        // calling parent constructor
        super(config, cache, id);

        // init default values and define getters and setters
        this.addDefaults_({
            minX: -180,
            maxX: 180,
            minY: -90,
            maxY: 90,
            getFeatureInfoName: null,
            availableStyles: {},
            // overwrite existing defaults
            opacitySettings: true,
            styleSettings: true,
            icon: "vef vef-raster"
        });

        this.setAll_("type", "wms");

        // internal properties
        this.zIndex_ = 0;
        this.currentQueryParameters_ = {};
    }

    /**
     * create a map layer based on the service options
     * 
     * @param {string} type e.g. "leaflet"
     * @private
     */
    createMapLayer_(type) {
        let layer = null;
        switch (type) {
            case "leaflet":
                layer = L.tileLayer.wms(this.serviceUrl, {
                    layers: this.getRequestName(),
                    version: this.serviceVersion,
                    attribution: this.attribution,
                    format: 'image/png',
                    transparent: true,
                    continuousWorld: false,
                    maxZoom: 30
                });

                break;
            case "three-globe":
                layer = new EventObject({
                    "loading": [],
                    "load": [],
                    "redraw_wms": []
                });

                break;
        }

        if (layer) {
            layer.on('loading', () => this.fire("loading"));
            layer.on('load', () => this.fire("load"));
            this.mapLayers_[type] = layer;
        }

        return layer;
    }

    /**
     * Helper method to update the mapLayer according to
     * the current state. Might be necessary after switching the map type
     * 
     * @private
     */
    updateMapLayer_(type) {
        this.activeMap_ = type;

        // call setters to apply options to the active mapLayer
        if (Number.isFinite(this.opacity)) this.setOpacity(this.opacity);
        if (Number.isFinite(this.zIndex_)) this.setZIndex(this.zIndex_);
        this.setProjection(this.crs_);

        // set the color scale or a style if defined
        if (this.colorScale) {
            this.setColorScale(this.colorScale, true);
        } else if (this.staticQueryParameters.styles) {
            this.setStyle(this.staticQueryParameters.styles, true);
        } else if ((this.serviceSoftware == "rasdaman") && !this.getStyle() && (Object.keys(this.availableStyles).length > 0)) {
            // apply first style for rasdaman, because the default style is not returned automatically be the service
            this.setStyle(Object.keys(this.availableStyles)[0], true);
        } else {
            this.setStyle("", true);
        }

        // apply filter, static filter and static query parameters
        this.applyFilter(true);

        // reload was intentionally prevented in the setters to call it in the end
        this.reload();
    }

    /**
     * Print the Metadata from this layer as html.
     * @override
     */
    printMetadata() {
        const container = super.printMetadata();

        container.insertAdjacentHTML("beforeend", "<h3>Legend Graphic</h3>");
        container.insertAdjacentElement("beforeend", this.getLegendGraphic());

        return container;
    }

    /**
     * Returns the Legend Graphic for this layer.
     * May depend on current color scale.
     * 
     * @returns html <img>-tag (null or undefined if there is no legend graphic)
     */
    getLegendGraphic() {
        let url = this.legendUrl || `${this.serviceUrl}?REQUEST=GetLegendGraphic`;
        const urlLowerCase = url.toLowerCase();

        const parts = url.split("?");
        if (urlLowerCase.includes("request=getlegendgraphic") && (parts.length == 2)) {
            const params = parts[1].split("&");
            const style = this.getStyle();

            // set current style if the predefined style is empty.
            // cannot be done with "addParam" because "style=" already exists as an empty property
            for (let i = 0; i < params.length; ++i) {
                if (params[i].toLowerCase() == "style=") {
                    params[i] += encodeURIComponent(style);
                }
            }

            // only add param if it does not exist
            const addParam = (key, value) => {
                if (value && !urlLowerCase.includes(key.toLowerCase() + "=")) {
                    params.push(key + "=" + encodeURIComponent(value));
                }
            };

            addParam("SERVICE", "WMS");
            addParam("LAYER", this.getRequestName());
            addParam("STYLE", style);
            addParam("ENV", this.currentQueryParameters_.env);
            addParam("TRANSPARENT", "true");
            addParam("FORMAT", "image/png");
            addParam("VERSION", this.serviceVersion);
            addParam("LEGEND_OPTIONS", "forceLabels:on;fontSize:16;fontAntiAliasing:true;fontName:PT Sans");

            url = parts[0] + ((params.length) ? ("?" + params.join("&")) : "");
        }

        const image = document.createElement("img");
        image.src = url;

        return image;
    }

    /**
     * Switches the projection for the visualization
     * of the leaflet layer. CRS hast to be included in availableCRS
     * 
     * @param {string} crs target crs code
     * @returns {boolean} returns true if successful
     */
    setProjection(crs) {
        const code = crs.replace(":", "");
        if (this.availableCrs.includes(crs) && (code in L.CRS)) {
            this.crs_ = crs;
            if (this.activeMap_ in this.mapLayers_) {
                switch (this.activeMap_) {
                    case "leaflet":
                        this.mapLayers_.leaflet.options.crs = L.CRS[code];
                        this.mapLayers_.leaflet.options.layers = this.getRequestName();
                        this.mapLayers_.leaflet.wmsParams.layers = this.getRequestName();
                }
            }
            return true;
        }
        return false;
    }

    /**
     * Set the style of the layer. Does not check if a style exists in availableStyles (non-listed styles are allowed)
     * 
     * @param {string} styleName style name
     * @param {boolean} noReload prevents reload if true
     */
    setStyle(styleName, noReload) {
        // remove "env" from colorscale settings
        if (typeof this.staticQueryParameters.env == "string") delete this.staticQueryParameters.env;
        if (typeof this.currentQueryParameters_.env == "string") delete this.currentQueryParameters_.env;

        this.staticQueryParameters.styles = styleName || "";
        this.currentQueryParameters_.styles = styleName || "";
        this.colorScale = null;

        if (!noReload) this.reload();
    }

    /**
     * Get the active style of the layer.
     */
    getStyle() {
        return ("styles" in this.staticQueryParameters) ? this.staticQueryParameters.styles : "";
    }

    /**
     * Set the colorscale for the layer
     * 
     * @param colorScale 
     * @param {boolean} noReload prevents reload if true
     */
    setColorScale(colorScale, noReload) {
        this.colorScale = copyColorScale(colorScale);

        const params = colorScaleToWmsParams(colorScale);
        Object.assign(this.staticQueryParameters, params);
        Object.assign(this.currentQueryParameters_, params);

        if (!noReload) this.reload();
    }

    /**
     * Get the active colorscale for the layer
     */
    getColorScale() {
        return (this.colorScale) ? copyColorScale(this.colorScale) : null;
    }

    /**
     * Reload the Layer on the map
     */
    reload() {
        if (!(this.activeMap_ in this.mapLayers_)) return;

        const params = this.currentQueryParameters_;
        // apply the current query parameters to the layer
        switch (this.activeMap_) {
            case "leaflet":
                for (let key in params) {
                    if (params[key] !== this.mapLayers_.leaflet.wmsParams[key]) {
                        if (params[key]) {
                            this.mapLayers_.leaflet.wmsParams[key] = params[key];
                        } else if (this.mapLayers_.leaflet.wmsParams[key]) {
                            delete this.mapLayers_.leaflet.wmsParams[key];
                        }
                    }
                }
                this.mapLayers_.leaflet.redraw();
                break;
            case "three-globe":
                this.mapLayers_["three-globe"].fire("redraw_wms", this);
                break;
        }
    }

    /**
     * Apply a filter on the layer. Uses WMS filter string
     * 
     * @param {boolean} noReload prevents reload if true
     */
    applyFilter(noReload) {
        let params = {};

        if (this.filter) Object.assign(params, this.createFilterParams_(this.filter));

        let changed = false;
        for (let key in params) {
            params[key] = (typeof params[key] == "string") ? params[key].trim() : null;
            if (this.currentQueryParameters_[key] !== params[key]) {
                changed = true;
                if (params[key]) {
                    this.currentQueryParameters_[key] = params[key];
                } else if (this.currentQueryParameters_[key]) {
                    delete this.currentQueryParameters_[key];
                }
            }
        }

        if (!noReload && changed) this.reload();
    }

    /**
     * Requests the "getFeatureInfo" method defined by url and responses an
     * array of objects containing the original data and popup styled content.
     *
     * @private
     * @param {string} url
     */
    requestFeatureInfo_(url) {
        return new Promise(resolve => {
            const xhttp = new XMLHttpRequest();

            xhttp.onreadystatechange = () => {
                if (xhttp.readyState == 4) {
                    const type = xhttp.getResponseHeader('content-type');
                    let response = null;

                    if (xhttp.status == 200) {
                        try {
                            if (type.indexOf('application/json') == 0) {
                                response = JSON.parse(xhttp.responseText);
                            } else if ((type.indexOf('text/xml') == 0) || (type.indexOf('application/xml') == 0) || (type.indexOf('xml') > 0)) {
                                response = xmlToJson(xhttp.responseText)
                            } else {
                                response = xhttp.responseText;
                            }
                        } catch (e) {
                            console.warn("Could not parse " + type + " response!", e);
                        }
                    } else {
                        console.warn("HTTP status code is " + xhttp.status);
                    }

                    resolve(response);
                }
            };
            xhttp.open("GET", url, true);
            xhttp.send();
        });
    }

    /**
     * Get feature info for a specific location.
     * e.g. for displaying it in a map-popup
     * 
     * @param {object} options getFeatureInfo params from map 
     * @returns {Promise} feature info promise
     */
    getFeatureInfo(options) {
        return new Promise(resolve => {
            const software = this.serviceSoftware.toLowerCase();
            const layers = this.getFeatureInfoName || this.getRequestName();

            // return empty response if software is not supported
            if (!["esri", "geoserver"].includes(software)) resolve({ layer: this, data: null });

            const params = {
                service: "WMS",
                request: "GetFeatureInfo",
                styles: this.getStyle(),
                layers: layers,
                query_layers: layers,
                width: options.width,
                height: options.height,
                feature_count: 50
            };

            // handle software specific cases
            if (software == "esri") {
                Object.assign(params, {
                    info_format: this.infoFormat || "text/xml",
                    version: "1.1.1"
                });
            } else if (software == "geoserver") {
                params.info_format = this.infoFormat || "application/json";
                params.version = this.serviceVersion;
                params.buffer = 50;

                // sort results by the correct time column
                let timeColumn = null;
                for (let i = 0; i < this.timeColumns_.length; ++i) {
                    const column = this.timeColumns_[i];
                    if ((column in this.dimensions) && (this.dimensions[column].type == "attribute_field")) {
                        timeColumn = this.dimensions[column].name || column;
                    } else if (column in this.attributeFields) {
                        timeColumn = column;
                    }
                    if (timeColumn) {
                        params.sortBy = timeColumn + " A";
                        break;
                    }
                }
            }

            // version specific adjustments
            const min = options.bbox.min;
            const max = options.bbox.max;
            params.bbox = ((params.version === "1.3.0") && (options.bbox.crs == "EPSG:4326"))
                ? [min.y, min.x, max.y, max.x].join(",")
                : [min.x, min.y, max.x, max.y].join(",")
                ;
            params[params.version === "1.3.0" ? "I" : "X"] = options.x;
            params[params.version === "1.3.0" ? "J" : "Y"] = options.y;
            params[params.version === "1.3.0" ? "CRS" : "SRS"] = options.bbox.crs;

            // only add filter and time dimension if defined
            if (this.currentQueryParameters_.filter) params.filter = this.currentQueryParameters_.filter;
            if (this.currentQueryParameters_.time) params.time = this.currentQueryParameters_.time;

            // build URL
            const baseUrl = this.serviceUrl + "?";
            const url = baseUrl + L.Util.getParamString(params, baseUrl, true);

            console.log("The URL queried is: ", url);

            this.requestFeatureInfo_(url).then(response => resolve({ layer: this, data: response }));
        });

    }

    /**
     * Set the transparency between 0 and 1
     * @override
     * @param {number} opacity style name
     */
    setOpacity(opacity) {
        this.opacity = opacity;

        if (!(this.activeMap_ in this.mapLayers_)) return;

        switch (this.activeMap_) {
            case "leaflet":
                this.mapLayers_.leaflet.setOpacity(opacity);
                break;
            case "three-globe":
                this.mapLayers_["three-globe"].fire("redraw_wms", this);
                break;
        }
    }

    /**
     * Set the z-index of a layer
     * @param {number} index 
     */
    setZIndex(index) {
        this.zIndex_ = index;

        if (!(this.activeMap_ in this.mapLayers_)) return;

        switch (this.activeMap_) {
            case "leaflet":
                this.mapLayers_.leaflet.setZIndex(index);
                break;
            case "three-globe":
                this.mapLayers_["three-globe"].fire("redraw_wms", this);
                break;
        }
    }

    /**
     * Get the bounding box as an object
     * 
     * @returns {object} { min: {x, y}, max: {x, y}, crs: "CRS:84" }
     */
    getBounds() {
        return {
            min: {
                x: (this.minX < this.maxX) ? this.minX : this.maxX,
                y: (this.minY < this.maxY) ? this.minY : this.maxY

            },
            max: {
                x: (this.minX > this.maxX) ? this.minX : this.maxX,
                y: (this.minY > this.maxY) ? this.minY : this.maxY
            },
            crs: "CRS:84"
        }
    }

}
